import React, { ReactElement, useEffect, useState } from 'react';
import { Entente, HistoriquePrixProduit, Produit } from '../../../../../../common/src/entities';
import ChampFormulaire from '../../../Common/ChampFormulaire';
import CheckboxFormulaire from '../../../Common/CheckboxFormulaire';
import { FormulaireCreationEntite, PropsDeCreationEntite } from '../../Utils/FormulaireCreationEntite';
import { MyPaper } from '../../Utils/MyPaper';
import ActiviteEntenteProduit from '../../../../../../common/src/entities/activite-entente-produit.entity';
import { useFormikContext } from 'formik';
import ActiviteEntente from '../../../../../../common/src/entities/activite-entente.entity';
import ChampSelectEntite from '../../Utils/ChampSelectEntite';
import { isNumber } from 'lodash';
import Facturation from '../../../../../../common/src/entities/facturation.entity';

const LABEL_COL = 4;

type DetailsActiviteEntenteProduitProps = {
    indexActiviteEntente?: number;
    indexActiviteEntenteProduit: number;
    entente: Entente;
};

export function ChampsActiviteEntenteProduit(props: DetailsActiviteEntenteProduitProps): ReactElement {
    const { indexActiviteEntente, indexActiviteEntenteProduit, entente } = props;
    const { values, setFieldValue, setValues } = useFormikContext<ActiviteEntenteProduit>();

    useEffect(() => {
        if (isNumber(indexActiviteEntente)) {
            setValues(
                entente.activiteEntentes[indexActiviteEntente].activiteEntenteProduit[indexActiviteEntenteProduit],
            );
        }
    }, [indexActiviteEntente]);

    return (
        <div className="pb-5">
            <MyPaper>
                {entente.activiteEntentes && (
                    <>
                        <ChampSelectEntite
                            id="activiteEntente"
                            initialValue={
                                isNumber(indexActiviteEntente)
                                    ? entente.activiteEntentes[indexActiviteEntente]
                                    : undefined
                            }
                            label="Activité"
                            items={entente.activiteEntentes}
                            titreBouton="Choisir une activité"
                            labelCol={LABEL_COL}
                            onChange={(item: ActiviteEntente | null) => {
                                setFieldValue('activiteEntente', item);
                            }}
                            affichageItem={(item: ActiviteEntente) => item.activite.name}
                            keyEntite={ActiviteEntente.key}
                            getItemName={(item: ActiviteEntente) => item.activite.name}
                            getItemLabel={(item: ActiviteEntente) =>
                                item.activite.name + ' (' + item.activite.fournisseur?.name + ')'
                            }
                        />

                        {values.activiteEntente && (
                            <>
                                <ChampSelectEntite
                                    id="produit"
                                    label="Produit"
                                    initialValue={
                                        isNumber(indexActiviteEntente)
                                            ? entente.activiteEntentes[indexActiviteEntente].activiteEntenteProduit[
                                                  indexActiviteEntenteProduit
                                              ].produit
                                            : undefined
                                    }
                                    items={
                                        entente.activiteEntentes
                                            .find((ae) => ae.id == values.activiteEntente.id)
                                            ?.activite.activiteProduits.map((ap) => ap.produit) ?? []
                                    }
                                    titreBouton="Choisir un produit"
                                    labelCol={LABEL_COL}
                                    onChange={(item: Produit | null) => {
                                        let prix = item?.historique_prix_produit
                                            ?.filter(
                                                (prix) => prix.date_debut && new Date(prix.date_debut) <= new Date(),
                                            )
                                            .sort(
                                                (a, b) =>
                                                    new Date(b.date_debut).getTime() - new Date(a.date_debut).getTime(),
                                            )[0];
                                        if (prix && prix.facturation) {
                                            delete (prix.facturation as Partial<Facturation>).id;
                                        }
                                        setFieldValue('historique_prix_produit', prix);
                                        setFieldValue('facturation', prix?.facturation);
                                        setFieldValue('produit', item);
                                    }}
                                    affichageItem={(item: Produit) => item?.name}
                                    keyEntite={Produit.key}
                                    getItemName={(item: Produit) => item?.name}
                                    getItemLabel={(item: Produit) => item?.name}
                                />

                                {values.produit && (
                                    <>
                                        <ChampSelectEntite
                                            id="historique_prix_produit"
                                            label="Prix"
                                            items={
                                                values.produit.historique_prix_produit
                                                    ? values.produit.historique_prix_produit.sort((a, b) => {
                                                          const dateA = a.date_debut
                                                              ? new Date(a.date_debut).getTime()
                                                              : Number.MAX_SAFE_INTEGER;
                                                          const dateB = b.date_debut
                                                              ? new Date(b.date_debut).getTime()
                                                              : Number.MAX_SAFE_INTEGER;

                                                          return dateB - dateA;
                                                      })
                                                    : []
                                            }
                                            titreBouton="Choisir un prix"
                                            labelCol={LABEL_COL}
                                            onChange={(item: Partial<HistoriquePrixProduit> | null) => {
                                                setFieldValue(`facturation`, item?.facturation);
                                            }}
                                            initialValue={values.historique_prix_produit}
                                            affichageItem={(item: HistoriquePrixProduit) => item.date_debut}
                                            keyEntite={HistoriquePrixProduit.key}
                                            getItemName={(item: HistoriquePrixProduit) => item.date_debut}
                                            getItemLabel={(item: HistoriquePrixProduit) => item.date_debut}
                                        />

                                        <CheckboxFormulaire
                                            id={`facturation.b_taxable`}
                                            titre="Taxable"
                                            isChecked={values.facturation?.b_taxable}
                                        />
                                        <ChampFormulaire
                                            id={`facturation.methode_facturation`}
                                            titre="Méthode de facturation"
                                            suggestions={['Par participant', 'Par Groupe', 'Forfaitaire', "À l'heure"]}
                                            labelCol={LABEL_COL}
                                        />
                                        <ChampFormulaire
                                            id={`facturation.prix_de_vente_fournisseur`}
                                            titre="Prix de vente du fournisseur"
                                            type="number"
                                            labelCol={LABEL_COL}
                                        />
                                        <ChampFormulaire
                                            id={`facturation.valeur_commission_dollar`}
                                            titre="Commission $"
                                            type="number"
                                            labelCol={LABEL_COL}
                                        />
                                        <ChampFormulaire
                                            id={`facturation.valeur_commission_pourcentage`}
                                            titre="Commission %"
                                            type="number"
                                            labelCol={LABEL_COL}
                                        />
                                        <ChampFormulaire
                                            id={`facturation.valeur_surfacturation_dollar`}
                                            titre="Surfacturation $"
                                            type="number"
                                            labelCol={LABEL_COL}
                                        />
                                        <ChampFormulaire
                                            id={`facturation.valeur_surfacturation_pourcentage`}
                                            titre="Surfacturation %"
                                            type="number"
                                            labelCol={LABEL_COL}
                                        />
                                    </>
                                )}
                            </>
                        )}
                    </>
                )}
            </MyPaper>
        </div>
    );
}

export type DetailsHistoriquePrixProduitProps = Partial<PropsDeCreationEntite<ActiviteEntenteProduit>> & {
    succes: (produit: ActiviteEntenteProduit) => void;
    indexActiviteEntenteProduit: number;
    indexActiviteEntente?: number;
    entente: Entente;
};

export default function DetailsActiviteEntenteProduit(props: DetailsHistoriquePrixProduitProps): ReactElement {
    const { succes, indexActiviteEntente, indexActiviteEntenteProduit, entente } = props;
    return (
        <FormulaireCreationEntite
            modal
            noSave
            texteEntete={(t: ActiviteEntenteProduit) => (
                <>
                    <small className="text-muted">Modifier</small>&nbsp;{t.name}
                </>
            )}
            entite={ActiviteEntenteProduit}
            valeursInitiales={
                indexActiviteEntente
                    ? entente.activiteEntentes[indexActiviteEntente].activiteEntenteProduit[indexActiviteEntenteProduit]
                    : {}
            }
            succes={succes}
        >
            <ChampsActiviteEntenteProduit
                entente={entente}
                indexActiviteEntente={indexActiviteEntente}
                indexActiviteEntenteProduit={indexActiviteEntenteProduit}
            />
        </FormulaireCreationEntite>
    );
}
